import 'reflect-metadata';
import { inject, injectable } from 'inversify';
import { ProviderIdentity } from 'openfin/_v2/shapes/Identity';
import { objectToAction } from '@openfin/predux';

import {
  APITopic,
  API,
  ClearPayload,
  CreatePayload,
  NotificationInternal,
  Events,
  UpdatePayload,
} from '../client/internal';
import { ProviderStatus } from '../client/provider';

import { Injector } from './common/Injector';
import { Inject } from './common/Injectables';
import { NotificationCenter } from './controller/NotificationCenter';
import { ToastManager } from './controller/ToastManager';
import { ExpiryController } from './controller/ExpiryController';
import { APIHandler } from './model/APIHandler';
import { StoredNotification } from './model/StoredNotification';
import {
  CreateNotification,
  RegisterStream,
  RemoveNotifications,
  ToggleCenterVisibility,
  ToggleCenterVisibilitySource,
  UpdateNotification,
} from './store/Actions';
import { mutable } from './store/State';
import { EventPump } from './model/EventPump';
import { ClientRegistry } from './model/ClientRegistry';
import { Database } from './model/database/Database';
import { ServiceStore } from './store/ServiceStore';
import { Persistor } from './controller/Persistor';
import { ClientEventController } from './controller/ClientEventController';
import { TrayIcon } from './model/TrayIcon';
import { WebWindowFactory } from './model/WebWindow';
import { Environment } from './model/Environment';
import { Layouter } from './controller/Layouter';
import { MonitorModel } from './model/MonitorModel';
import { getTemplateAPIVersion, getVersion } from './utils/version';
import { centerHistory } from './view/contexts/CenterHistory';
import { encodeId, hydrateNotification, hydrateUpdateNotification } from './utils';
import { WindowManager } from './controller/WindowManager';
import { FrameManager } from './controller/FrameManager';
import { getUpdateValidateOptions } from './utils/templates/getUpdateValidateOptions';

@injectable()
export class Main {
  private readonly _config = null;
  private readonly _apiHandler: APIHandler<APITopic, Events>;
  private readonly _clientEventController: ClientEventController;
  private readonly _clientRegistry: ClientRegistry;
  private readonly _database: Database;
  private readonly _environment: Environment;
  private readonly _eventPump: EventPump;
  private readonly _expiryController: ExpiryController;
  private readonly _frameManager: FrameManager;
  private readonly _layouter: Layouter;
  private readonly _monitorModel: MonitorModel;
  private readonly _notificationCenter: NotificationCenter;
  private readonly _persistor: Persistor;
  private readonly _store: ServiceStore;
  private readonly _toastManager: ToastManager;
  private readonly _trayIcon: TrayIcon;
  private readonly _webWindowFactory: WebWindowFactory;
  private readonly _windowManager: WindowManager;

  constructor(
    @inject(Inject.API_HANDLER) apiHandler: APIHandler<APITopic, Events>,
    @inject(Inject.CLIENT_EVENT_CONTROLLER) clientEventController: ClientEventController,
    @inject(Inject.CLIENT_REGISTRY) clientRegistry: ClientRegistry,
    @inject(Inject.DATABASE) database: Database,
    @inject(Inject.ENVIRONMENT) environment: Environment,
    @inject(Inject.EVENT_PUMP) eventPump: EventPump,
    @inject(Inject.EXPIRY_CONTROLLER) expiryController: ExpiryController,
    @inject(Inject.FRAME_MANAGER) frameManager: FrameManager,
    @inject(Inject.LAYOUTER) layouter: Layouter,
    @inject(Inject.MONITOR_MODEL) monitorModel: MonitorModel,
    @inject(Inject.NOTIFICATION_CENTER) notificationCenter: NotificationCenter,
    @inject(Inject.PERSISTOR) persistor: Persistor,
    @inject(Inject.STORE) store: ServiceStore,
    @inject(Inject.TOAST_MANAGER) toastManager: ToastManager,
    @inject(Inject.TRAY_ICON) trayIcon: TrayIcon,
    @inject(Inject.WEB_WINDOW_FACTORY) webWindowFactory: WebWindowFactory,
    @inject(Inject.WINDOW_MANAGER) windowManager: WindowManager
  ) {
    this._apiHandler = apiHandler;
    this._clientEventController = clientEventController;
    this._clientRegistry = clientRegistry;
    this._database = database;
    this._environment = environment;
    this._eventPump = eventPump;
    this._expiryController = expiryController;
    this._frameManager = frameManager;
    this._layouter = layouter;
    this._monitorModel = monitorModel;
    this._notificationCenter = notificationCenter;
    this._persistor = persistor;
    this._store = store;
    this._toastManager = toastManager;
    this._trayIcon = trayIcon;
    this._webWindowFactory = webWindowFactory;
    this._windowManager = windowManager;
  }

  public async register(): Promise<void> {
    Object.assign(window, {
      main: this,
      injector: Injector,
      config: this._config,
      apiHandler: this._apiHandler,
      center: this._notificationCenter,
      clientEventController: this._clientEventController,
      clientRegistry: this._clientRegistry,
      database: this._database,
      environment: this._environment,
      eventPump: this._eventPump,
      expiryController: this._expiryController,
      frameManager: this._frameManager,
      layouter: this._layouter,
      monitorModel: this._monitorModel,
      notificationCenter: this._notificationCenter,
      persitor: this._persistor,
      store: this._store,
      toast: this._toastManager,
      trayIcon: this._trayIcon,
      webWindowFactory: this._webWindowFactory,
      centerHistory,
      WindowManager: this._windowManager,
      predux: {
        // Expose Action deserialization to tests
        objectToAction,
      },
    });

    // Wait for creation of any injected components that require async initialization
    await Injector.init();

    // Current API
    this._apiHandler.registerListeners<API>({
      [APITopic.CREATE_NOTIFICATION]: this.createNotification.bind(this),
      [APITopic.UPDATE_NOTIFICATION]: this.updateNotification.bind(this),
      [APITopic.CLEAR_NOTIFICATION]: this.clearNotification.bind(this),
      [APITopic.GET_APP_NOTIFICATIONS]: this.fetchAppNotifications.bind(this),
      [APITopic.CLEAR_APP_NOTIFICATIONS]: this.clearAppNotifications.bind(this),
      [APITopic.TOGGLE_NOTIFICATION_CENTER]: this.toggleNotificationCenter.bind(this),
      [APITopic.ADD_EVENT_LISTENER]: this._clientRegistry.onAddEventListener.bind(
        this._clientRegistry
      ),
      [APITopic.REMOVE_EVENT_LISTENER]: this._clientRegistry.onRemoveEventListener.bind(
        this._clientRegistry
      ),
      [APITopic.GET_PROVIDER_STATUS]: this.getStatus.bind(this),
      [APITopic.GET_NOTIFICATIONS_COUNT]: this.getNotificationsCount.bind(this),
    });

    console.log('Service Initialised');
  }

  private async getNotificationsCount(): Promise<number> {
    return this._store.state.notifications.length;
  }

  /**
   * Create the notification and dispatch it to the UI
   * @param payload The contents to be dispatched to the UI
   * @param sender Window info for the sending client. This can be found in the relevant app.json within the demo folder.
   */
  private async createNotification(
    payload: CreatePayload,
    sender: ProviderIdentity
  ): Promise<NotificationInternal> {
    // Explicitly create the identity object to avoid storing other unneeded info from ProviderIdentity
    const notification = hydrateNotification(payload, {
      type: 'desktop',
      identity: { uuid: sender.uuid, name: sender.name },
    });
    await this.handleStreamStatus(notification);
    await this._store.dispatch(new CreateNotification(notification));

    return mutable(notification.notification);
  }

  private async updateNotification(payload: UpdatePayload): Promise<NotificationInternal> {
    const existingNotification:
      | StoredNotification
      | undefined = this._store.state.notifications.find(
      (nfn) => nfn.notification.id === payload.id
    );

    if (!existingNotification) {
      throw new Error(`Cannot find existing notification to update with id: ${payload.id}`);
    }

    const validatedPayload = hydrateUpdateNotification(
      payload,
      getUpdateValidateOptions(existingNotification?.notification)
    );

    await this._store.dispatch(new UpdateNotification(validatedPayload));
    const updatedNotification = this._store.state.notifications.find(
      (nfn) => nfn.notification.id === payload.id
    );
    if (!updatedNotification) {
      // shouldn't really happen
      throw new Error(`Cannot find updated notification with id: ${payload.id}`);
    }
    return mutable(updatedNotification.notification);
  }

  /**
   * Dispatch the Notification Center toggle action to the UI
   */
  private async toggleNotificationCenter(): Promise<void> {
    await this._store.dispatch(new ToggleCenterVisibility(ToggleCenterVisibilitySource.API));
  }

  /**
   * Tell the UI to delete a specific notification
   * @param payload Should contain the id of the notification we want to delete. Maybe should just be a string
   * @param sender Window info for the sending client. This can be found in the relevant app.json within the demo folder.
   * @returns Whether or not the removal of the notifications was successful.
   */
  private async clearNotification(
    payload: ClearPayload,
    sender: ProviderIdentity
  ): Promise<boolean> {
    if (typeof payload.id !== 'string') {
      throw new Error('Invalid argument passed to clear: argument must be a string');
    }

    const id = encodeId(payload.id, sender);
    const notification = this._store.state.notifications.find((n) => n.id === id);
    if (notification) {
      await this._store.dispatch(new RemoveNotifications([notification]));
      return true;
    }
    return false;
  }

  /**
   * This allows you to fetch apps via your uuid
   * @param payload The payload can contain the uuid
   * @param sender The sender info contains the uuid of the sender
   */
  private fetchAppNotifications(
    payload: undefined,
    sender: ProviderIdentity
  ): NotificationInternal[] {
    const notifications = this.getAppNotifications(sender.uuid);

    return notifications.map((notification) => mutable(notification.notification));
  }

  private async clearAppNotifications(
    payload: undefined,
    sender: ProviderIdentity
  ): Promise<number> {
    const notifications = this.getAppNotifications(sender.uuid);
    await this._store.dispatch(new RemoveNotifications(notifications));

    return notifications.length;
  }

  private getAppNotifications(uuid: string): StoredNotification[] {
    const notifications = this._store.state.notifications;
    return notifications.filter(
      (n) => n.source.type === 'desktop' && n.source.identity.uuid === uuid
    );
  }

  private async handleStreamStatus(notification: StoredNotification): Promise<void> {
    const stream = notification.notification.stream;

    if (stream) {
      const existingStream = this._store.state.streams.get(stream.id);

      if (!existingStream || existingStream.displayName !== stream.displayName) {
        const appId = notification.source.identity.uuid;
        await this._store.dispatch(new RegisterStream(stream, appId));
      }
    }
  }

  private getStatus(): ProviderStatus {
    return {
      connected: true,
      version: getVersion(),
      templateAPIVersion: getTemplateAPIVersion(),
    };
  }
}

// Start service provider
Injector.getClass(Main).register();
